<template>
  <section class="confirm-code-container">
    <FloatingHeader
      :title="'Verificação de Código'"
      :showHeader="true"
      :hideSearch="true"
      :routeToBack="`/qrcode-checkout/${productId}`"
    ></FloatingHeader>

    <h1>Por favor informe o código enviado para o seu e-mail e número de telefone.</h1>

    <div class="confirm-code">
      <div class="code-inputs">
        <input
          v-for="(digit, index) in codeArray"
          :key="index"
          ref="codeInput"
          class="code-input"
          type="number"
          maxlength="1"
          pattern="[0-9]*"
          :class="{ filled: codeArray[index] !== '' }"
          v-model="codeArray[index]"
          @input="handleInput(index, $event)"
          @keydown.backspace="handleBackspace(index, $event)"
          @paste="handlePaste"
        />
      </div>

      <div class="resend-code">
        <p>Não recebeu o código?
          <span
            @click="resend"
            :style="{ pointerEvents: cooldownSeconds > 0 ? 'none' : 'auto', opacity: cooldownSeconds > 0 ? 0.5 : 1 }"
          >
            {{ cooldownSeconds > 0 ? `Reenviar (${cooldownSeconds}s)` : 'Reenviar' }}
          </span>
        </p>
      </div>

      <div class="confirm-code-button" v-if="!loading">
        <DefaultButton
          v-for="(but, bKey) in buttons"
          :key="bKey"
          :text="but.display"
          @defaultBtnClick="but.action && but.action()"
        ></DefaultButton>
      </div>
    </div>
    <ModalInfo></ModalInfo>
    <ScreenLoader :isLoading="loading" :loadingText="loadingText" />
  </section>
</template>

<script>
import FloatingHeader from '@/components/FloatingHeader.vue'
import DefaultButton from '@/components/buttons/DefaultButton.vue'
import ModalInfo from '@/components/modals/ModalInfo.vue'
import ScreenLoader from '@/components/loader/ScreenLoader.vue'
import Util from '@/assets/libs/util'
import ProductController from '@/assets/controllers/ProductController'
import CartController from '@/assets/controllers/CartController'
import { mapGetters, mapActions } from 'vuex'
import { sendOtp, validateOtp } from '@/assets/controllers/ConsumerController'
import { doOtpLogin } from '@/assets/controllers/LoginController'
import { loadMenu } from '@/assets/controllers/MenuController'

export default {
  name: 'ConfirmCodeView',
  components: {
    FloatingHeader,
    DefaultButton,
    ModalInfo,
    ScreenLoader
  },
  data () {
    return {
      codeArray: ['', '', '', ''],
      buttons: [
        {
          display: 'Continuar',
          fill: true,
          action: this.confirmCode
        }
      ],
      loading: false,
      loadingText: '',
      productId: '',
      cooldownSeconds: 0,
      cooldownTimer: null
    }
  },
  methods: {
    ...mapActions(['updateCurrentItemEdit', 'showModalInfo']),
    handleInput (index, event) {
      const value = event.target.value

      if (value.length > 1) {
        this.codeArray[index] = value[0]
      } else {
        this.codeArray[index] = value
      }

      // Se o usuário digitou um dígito e não estiver no último input,
      // movemos o foco para o próximo
      if (value && index < this.codeArray.length - 1) {
        this.$nextTick(() => {
          this.$refs.codeInput[index + 1].focus()
        })
      }
    },
    handleBackspace (index, event) {
      if (!this.codeArray[index] && index > 0) {
        this.$nextTick(() => {
          this.$refs.codeInput[index - 1].focus()
        })
      }
    },
    handlePaste (event) {
      event.preventDefault()
      const paste = (event.clipboardData || window.clipboardData).getData('text')
      const digits = paste.replace(/\D/g, '').slice(0, this.codeArray.length).split('')

      this.codeArray = this.codeArray.map((_, i) => digits[i] || '')

      this.$nextTick(() => {
        const nextIndex = digits.length < this.codeArray.length ? digits.length : this.codeArray.length - 1
        this.$refs.codeInput[nextIndex].focus()
      })
    },
    resend () {
      if (this.cooldownSeconds > 0) return

      const { email, phone } = this.getUserContact

      if (!email || !phone) {
        Util.showWarningNotification({
          title: 'Dados de contato não encontrados. Retorne para a tela anterior.',
          text: ''
        })
        return
      }

      this.loading = true
      this.loadingText = 'Reenviando código...'

      sendOtp({ email, phone })
        .then((response) => {
          this.loading = false

          if (response.error) {
            Util.showErrorNotification({
              title: 'Erro ao reenviar o código. Tente novamente.',
              text: ''
            })

            return
          }

          Util.showSuccessNotification({
            title: 'Código reenviado com sucesso!',
            text: ''
          })

          this.startCooldown()
        })
        .catch((error) => {
          console.error(error)
          this.loading = false
          Util.showErrorNotification({
            title: 'Erro inesperado ao reenviar o código. Tente novamente.',
            text: ''
          })
        })
    },
    confirmCode () {
      this.loading = true
      this.loadingText = 'Validando código...'

      const code = this.codeArray.join('')

      if (code.length < 4) {
        this.loading = false
        Util.showWarningNotification({
          title: 'Código incompleto.',
          text: ''
        })
        return
      }

      // valida o código com o backend
      const { email } = this.getUserContact

      validateOtp({ email, code }).then(response => {
        this.loading = false
        if (response.error) {
          this.showModalInfo({
            type: 'error',
            text: 'Código incorreto. Verifique seu e-mail ou SMS.',
            open: true
          })
          this.codeArray = ['', '', '', '']
          this.$nextTick(() => {
            this.$refs.codeInput[0].focus()
          })
          return
        }

        this.logUserIn()
      }).catch(error => {
        console.error(error)
        this.loading = false
        Util.showErrorNotification({
          title: 'Erro na verificação do código.',
          text: ''
        })
      })
    },
    logUserIn: function () {
      const { email, phone } = this.getUserContact

      if (!email || !phone) {
        Util.showWarningNotification({
          title: 'Dados de contato não encontrados. Retorne para a tela anterior.',
          text: ''
        })
        return
      }

      this.loading = true
      this.loadingText = 'Verificando dados...'

      doOtpLogin(this.getUserContact.email).then(response => {
        this.loading = false
        if (response.error) {
          this.showModalInfo({
            type: 'error',
            text: 'Erro ao validar dados. Verifique seus dados e tente novamente.',
            open: true
          })
          return
        }

        this.loadMenuCall()
      }).catch(error => {
        console.error(error.message || error)
        this.loading = false
        Util.showErrorNotification({
          title: 'Erro ao fazer login.',
          text: ''
        })
      })
    },
    loadMenuCall: async function () {
      this.loading = true
      this.loadingText = 'Carregando cardápio...'

      loadMenu().then((res) => {
        this.loading = false
        if (!res.data?.groups) {
          Util.showErrorNotification({
            title: 'Erro ao carregar o cardápio.',
            text: ''
          })
          return
        }

        this.redirectToCart()
      }).catch(error => {
        console.error(error)
        this.loading = false
        Util.showErrorNotification({
          title: 'Erro ao carregar o menu.',
          text: ''
        })
      })
    },
    redirectToCart: async function () {
      // Verifica se o produto existe no menu
      const menu = this.getMenu
      const allProducts = menu.groups.flatMap(group => group.products)
      const product = allProducts.find((p) => {
        return p.productCode === this.productId
      })

      if (!product) {
        Util.showErrorNotification({
          title: 'Produto não encontrado.',
          text: ''
        })
        return
      }

      // Verifica se o produto tem grupos de produtos e se algum deles tem quantidade minima maior que 0
      const requiresCustomSelection =
        (product.productGroups &&
        product.productGroups.length > 0 &&
        product.productGroups.some(group => group.productGroupMin > 0)) || (
          product.observationsChoice &&
          product.observationsChoice.some(option => option.minQuantity > 0)
        )

      if (requiresCustomSelection) {
        // Redireciona pra tela do produto
        this.updateCurrentItemEdit(ProductController.ajustProductToOpen(JSON.parse(JSON.stringify(product))))
        this.$router.push({
          name: 'productitem',
          query: {
            redirectToCart: true
          }
        })
      } else {
        // Produto simples -> adicionar direto ao carrinho
        const productCopy = {
          ...product,
          quantity: 1,
          totalItemPrice: product.price,
          observations: []
        }
        this.updateCurrentItemEdit(productCopy)
        CartController.addToCart(productCopy)

        Util.showSuccessNotification({
          title: 'Produto adicionado ao carrinho.',
          text: ''
        })

        this.$router.push('/cart')
      }
    },
    startCooldown () {
      this.cooldownSeconds = 30
      if (this.cooldownTimer) {
        clearInterval(this.cooldownTimer)
      }
      this.cooldownTimer = setInterval(() => {
        this.cooldownSeconds--
        if (this.cooldownSeconds <= 0) {
          clearInterval(this.cooldownTimer)
          this.cooldownTimer = null
        }
      }, 1000)
    }
  },
  computed: {
    ...mapGetters(['getMenu', 'getUserContact'])
  },
  mounted () {
    this.productId = this.$route.query.productId
  },
  beforeUnmount () {
    if (this.cooldownTimer) {
      clearInterval(this.cooldownTimer)
    }
  }
}
</script>

<style scoped>
.confirm-code-container {
  padding: 65px 20px 170px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  h1 {
    font-size: 1rem;
    font-weight: 500;
    text-align: center;
    margin-bottom: 2rem;
  }

  .confirm-code {
    margin-top: 1.2rem;
  }

  .code-inputs {
    display: flex;
    justify-content: center;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .code-input {
    width: 50px;
    height: 50px;
    font-size: 2rem;
    text-align: center;
    border: 1px solid #ccc;
    border-radius: 8px;
  }

  .code-input.filled {
    outline: 1px solid var(--primaryColor);
  }

  .resend-code {
    display: flex;
    justify-content: center;
    margin-bottom: 1rem;
    font-size: 0.9rem;
    color: var(--primaryColor);
    cursor: pointer;

    span {
      font-weight: bold;
      margin-left: 5px;
    }
  }

  .confirm-code-button {
    position: fixed;
    display: flex;
    justify-content: center;
    width: -webkit-fill-available;
    bottom: 0;
    left: 0;
    padding: 10px 0 50px 0;

    button {
      display: block;
      min-width: 80vw !important;
      height: 55px !important;
      margin: 5px !important;
      border: none;
      background: var(--primaryColor);
      color: white;
    }
  }
}

</style>
